import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import UserModal from 'components/UserModal'
import {
  selectCurrentUser,
  selectIsLoadingUsers,
  selectUsers,
  setIsInitialized,
  addAlert,
  fetchCurrentUser,
} from 'store/global'
import { usePermissions } from 'hooks/usePermissions'
import {
  selectDisplayedUser,
  setDisplayedUser,
  submitUser,
  updateUser,
  deleteUser,
  closeDisplayedUser,
} from 'store/organizations'
import messages from './messages'
import styles from './Users.module.scss'
import { orderBy } from 'lodash'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Loader from 'components/Loader'
import Search from 'UI/Search/Search'
import { Table, Empty, Button } from 'antd'
import { DeleteOutlined, EditOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons'

export default function Users() {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const currentUser = useSelector(selectCurrentUser)
  const isLoading = useSelector(selectIsLoadingUsers)
  const users = useSelector(selectUsers)
  const displayedUser = useSelector(selectDisplayedUser)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    dispatch(closeDisplayedUser())
    dispatch(setIsInitialized(true))
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredUsers = React.useMemo(() => {
    if (!searchTerm) return users
    return users.filter(user =>
      [user.firstName, user.lastName, user.email, user.organization].some(value =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    )
  }, [users, searchTerm])

  const sortedUsers = React.useMemo(() => {
    if (!sortColumn) return filteredUsers
    return orderBy(filteredUsers, [sortColumn], [sortOrder])
  }, [filteredUsers, sortColumn, sortOrder])

  const onDisplayModal = (action = 'create', existingUser = {}) => {
    const newUser = {
      ...existingUser,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedUser({ user: newUser }))
  }

  const onSubmit = async user => {
    try {
      if (displayedUser.action === 'delete') {
        if (displayedUser.email === currentUser.email) {
          await dispatch(
            addAlert({
              type: 'danger',
              message: `User ${displayedUser.email} cannot delete itself`,
            }),
          )
          dispatch(setDisplayedUser())
          return
        }
        const deleteResult = await dispatch(deleteUser({ userId: displayedUser.id }))
        if (deleteUser.fulfilled.match(deleteResult)) {
          dispatch(addAlert({ type: 'success', message: 'User deleted successfully' }))
        } else {
          dispatch(addAlert({ type: 'danger', message: 'Failed to delete user' }))
          return
        }
      } else if (displayedUser.action === 'edit') {
        const updatedUser = {
          ...user,
          id: displayedUser.id,
          organization: user.organization || currentUser.organizationId,
          role: user.role || 'basic-user',
          groupIds: user.groupIds || [],
        }
        const updateResult = await dispatch(updateUser({ user: updatedUser }))
        if (updateUser.fulfilled.match(updateResult)) {
          dispatch(addAlert({ type: 'success', message: 'User updated successfully' }))
        } else {
          dispatch(addAlert({ type: 'danger', message: 'Failed to update user' }))
          return
        }
      } else if (displayedUser.action === 'create') {
        const submittedUser = {
          ...user,
          organization: user.organization || currentUser.organizationId,
          role: user.role || 'basic-user',
          groupIds: user.groupIds || [],
        }
        const createResult = await dispatch(submitUser({ user: submittedUser }))

        if (submitUser.fulfilled.match(createResult)) {

          const responseData = createResult.payload
          
          if (responseData && (responseData.success === true || responseData.tempPassword)) {
            const tempPassword = responseData?.tempPassword
            
            dispatch(
              addAlert({
                type: 'success',
                message: `User created successfully! Temporary password: ${tempPassword}`,
              }),
            )
          } else {
            dispatch(addAlert({ 
              type: 'danger', 
              message: responseData?.message || 'Failed to create user - invalid response' 
            }))
            return
          }
        } else {
          const error = createResult.payload
          let errorMessage = 'Failed to create user'

          if (error?.response?.data) {
            const errorData = error.response.data
            
            if (errorData.errorMessage) {
              errorMessage = errorData.errorMessage
            } else if (errorData.error_description) {
              errorMessage = errorData.error_description
            } else if (errorData.message) {
              errorMessage = errorData.message
            } else if (typeof errorData === 'string') {
              errorMessage = errorData
            }
          }

          if (error?.response?.status === 409) {
            errorMessage = `User with email "${user.email}" already exists`
          } else if (error?.response?.status === 400) {
            if (!errorMessage || errorMessage === 'Failed to create user') {
              errorMessage = 'Invalid user data provided'
            }
          } else if (error?.response?.status === 401) {
            errorMessage = 'Unauthorized - you do not have permission to perform this action'
          } else if (error?.response?.status === 403) {
            errorMessage = 'Forbidden - insufficient permissions'
          } else if (error?.response?.status === 500) {
            errorMessage = errorMessage !== 'Failed to create user' ? errorMessage : 'Server error - please try again later'
          } else if (error?.message) {
            errorMessage = error.message
          }

          dispatch(addAlert({ type: 'danger', message: errorMessage }))
          return
        }
      }

      dispatch(setDisplayedUser())
      dispatch(fetchCurrentUser())
    } catch (error) {
      console.error('Unexpected error:', error)
      dispatch(addAlert({ type: 'danger', message: 'An unexpected error occurred' }))
    }
  }

  const onExportCSV = () => {
    if (!sortedUsers.length) return

    const exportData = sortedUsers.map(user => ({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      organization: user.organization || '',
      email: user.email || '',
    }))

    const fields = ['firstName', 'lastName', 'organization', 'email']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'users.csv')
  }

  const tableColumns = [
    {
      title: <FormattedMessage {...messages.columns.firstName} />,
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: true,
      sortOrder: sortColumn === 'firstName' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('firstName'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.lastName} />,
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: true,
      sortOrder: sortColumn === 'lastName' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('lastName'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.email} />,
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortOrder: sortColumn === 'email' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('email'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.organization} />,
      dataIndex: 'organization',
      key: 'organization',
      sorter: true,
      sortOrder: sortColumn === 'organization' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('organization'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.actions} />,
      key: 'actions',
      render: (_, record) => {
        const isManaged = record.membershipType === 'MANAGED'

        if (isManaged) {
          return (
            <div className={styles.actions}>
              <span className={styles.managedMessage}>
                <FormattedMessage {...messages.actions.managedByOrganization} />
              </span>
            </div>
          )
        }

        const isCurrentUser = record.email === currentUser.email
        const deleteClassName = `${styles.action} ${isCurrentUser ? styles.disabled : ''}`

        return (
          <div className={styles.actions}>
            <div 
              onClick={isCurrentUser ? undefined : () => onDisplayModal('delete', record)} 
              className={deleteClassName}
              title={isCurrentUser ? 'Cannot delete your own account' : 'Delete user'}
            >
              <DeleteOutlined />
            </div>
            <div onClick={() => onDisplayModal('edit', record)} className={styles.action}>
              <EditOutlined />
            </div>
          </div>
        )
      },
    },
  ]

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loaderContainer}>
          <Loader type='globalLoading' message='Loading users...' />
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerActions}>
        {permissions.admin && (
          <Button onClick={() => onDisplayModal('create')}>
            <PlusOutlined /> New User
          </Button>
        )}
        <div className={styles.headerActionsRight}>
          <Search
            placeholder='Search for...'
            enterButton
            allowClear
            style={{ width: 264 }}
            onSearch={value => setSearchTerm(value)}
          />
          <Button onClick={onExportCSV} disabled={!sortedUsers.length} type='primary'>
            <UploadOutlined /> Export CSV
          </Button>
        </div>
      </div>

      <Table
        columns={tableColumns}
        dataSource={sortedUsers}
        loading={isLoading}
        rowKey='id'
        scroll={{ y: 550 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: <Empty description='No users found' />,
        }}
      />

      {!!displayedUser.isOpen && <UserModal mode={displayedUser.action} onSubmit={onSubmit} />}
    </div>
  )
}
